import React, { useEffect, useState } from "react";
import { useBrandStore } from "../store/brandStore";
import {
  Button,
  Table,
  Card,
  Row,
  Col,
  Input,
  Form,
  Empty,
  Checkbox,
} from "antd";
import { PlusCircleOutlined, EditOutlined } from "@ant-design/icons";
import { RFMTier, RFMType } from "../constants/enums";
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { brandRFMSettingService } from "../services/brandRFMSettingService";
import { useBrandRFMSettingStore } from "../store/brandRFMSettingStore";
import CriteriaForm from "../components/common/Criteria";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutePaths } from "../routes/route";
import {
  BrandRFMProfile,
  BrandRFMProfileSetting,
} from "../models/brandRFMProfile";
import { brandRFMProfileService } from "../services/brandRFMProfileService";
import { useBrandRFMProfileStore } from "../store/brandRFMProfileStore";

interface TierData {
  tier: number;
  baseStart: number;
  baseEnd: number;
}

const EditRFMProfile: React.FC = () => {
  // =====================
  // State Declarations
  // =====================`

  // Hooks & Store
  const { brandId } = useBrandStore();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const profileId = searchParams.get("profileId");
  const { selectedBrandRFMProfile, setSelectedBrandRFMProfile } =
    useBrandRFMProfileStore();

  // UI & State Management
  const [loading, setLoading] = useState(false);
  const validateMessages = {
    required: "${label} is required!",
    types: {
      email: "${label} is not a valid email!",
      number: "${label} is not a valid number!",
    },
    number: {
      range: "${label} must be between ${min} and ${max}",
    },
  };
  const [selectedPeriod, setSelectedPeriod] = useState<number>(12);
  const [isCriteriaModalVisible, setIsCriteriaModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState<
    "Recency" | "Frequency" | "Monetary"
  >("Recency");
  const [isTyping, setIsTyping] = useState(false);
  const [isTypingEndValue, setIsTypingEndValue] = useState(false);
  const [defaultSetting, setDefaultSetting] = useState<any>([]);
  const [tooltipTexts, setTooltipTexts] = useState<Record<string, string>>({});
  const [rfmSettings, setRfmSettings] = useState<any[]>([]);
  const [brandRFMProfileSetting, setBrandRFMProfileSetting] = useState<any[]>(
    []
  );
  const [isActive, setIsActive] = useState(false);
  const [listProfile, setListProfile] = useState<any>([]);

  const optionsCutOffDateOnEdit = [
    {
      label: "Every 12 Months",
      value: moment()
        .subtract(12, "months")
        .subtract(1, "day")
        .format("YYYY-MM-DD"),
      period: 12,
    },
    {
      label: "Every 6 Months",
      value: moment()
        .subtract(6, "months")
        .subtract(1, "day")
        .format("YYYY-MM-DD"),
      period: 6,
    },
    {
      label: "Every 3 Months",
      value: moment()
        .subtract(3, "months")
        .subtract(1, "day")
        .format("YYYY-MM-DD"),
      period: 3,
    },
  ];
  const [cutOffDateInEdit, setCutOffDateInEdit] = useState<string>(
    moment().subtract(1, "year").subtract(1, "day").format("YYYY-MM-DD")
  );
  const { brandRFMSetting, setBrandRFMSetting } = useBrandRFMSettingStore();
  const [updatedBrandRFMSetting, setUpdatedBrandRFMSetting] = useState<any[]>(
    []
  );

  // Handlers
  const handleSubmitCriteria = (values: any) => {
    try {
      setLoading(true);
      const updatedSettings = updateRFMSettings(brandRFMProfileSetting, values);
      const fixedFrequencyData = getFrequencyTiers(
        updatedSettings ?? [],
        3,
        selectedPeriod
      );

      const finalUpdatedSettings = updatedSettings.map((item) => {
        if (item.RFMType === 2) {
          const matchedTier = fixedFrequencyData.find(
            (fixed) => fixed.RFMType === 2 && fixed.RFMTier === item.RFMTier
          );

          return {
            ...item,
            RFMStartValue: matchedTier?.RFMStartValue ?? item.RFMStartValue,
            RFMEndValue: matchedTier?.RFMEndValue ?? item.RFMEndValue,
          };
        }
        return item;
      });
      const transformedData = transformRFMData(updatedSettings);
      setBrandRFMProfileSetting(finalUpdatedSettings);
      setRfmSettings(transformedData);
      setIsCriteriaModalVisible(false);
    } catch {
      console.error("Error!");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitProfile = async (values: any) => {
    try {
      setLoading(true);
      const profile: BrandRFMProfile = {
        Id: selectedBrandRFMProfile ? selectedBrandRFMProfile.Id : 0,
        BrandId: brandId,
        Name: values.profile.name,
        Description: values.profile.description,
        IsActive: isActive,
        IsAuto: selectedBrandRFMProfile?.IsAuto ?? false,
        CreatedDate: new Date(),
        BrandRFMProfileSetting: brandRFMProfileSetting,
      };
      await brandRFMProfileService.updateBrandRFMProfile(profile);
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete("profileId");
      navigate(`${RoutePaths.IFRAMERFMPROFILE}?${searchParams.toString()}`);
    } catch {
      console.error("Error!");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsCriteriaModalVisible(false);
  };

  const onValuesChange = (changedValues: any, allValues: any) => {
    const updatedTooltips: Record<string, string> = {};

    Object.entries(changedValues).forEach(([key, raw]) => {
      const [typeStr, tierStr, position] = key.split("-");
      const type = parseInt(typeStr, 10);
      const tier = parseInt(tierStr, 10);

      let value =
        typeof raw === "string" || typeof raw === "number"
          ? parseFloat(raw.toString())
          : NaN;

      if (isNaN(value)) {
        const oppositePosition = position === "start" ? "end" : "start";
        const oppositeKey = `${type}-${tier}-${oppositePosition}`;
        const fallbackRaw = allValues[oppositeKey];
        if (fallbackRaw != null) {
          const fallbackValue = parseFloat(fallbackRaw.toString());
          if (!isNaN(fallbackValue)) {
            value = fallbackValue;
          } else {
            return;
          }
        } else {
          return;
        }
      }

      let step = 1;
      if (type === RFMType.Frequency) step = 0.1;
      if (type === RFMType.Monetary) step = 0.01;

      const fmt = (v: number) => {
        if (type === RFMType.Monetary)
          return Number.isInteger(v) ? `${v}` : v.toFixed(2);
        if (type === RFMType.Frequency)
          return Number.isInteger(v) ? `${v}` : v.toFixed(1);
        return `${v}`;
      };

      if (position === "end") {
        updatedTooltips[
          key
        ] = `Skip this to include values lower than ${value}`;
        const nextTier = type === RFMType.Recency ? tier + 1 : tier - 1;
        const nextKey = `${type}-${nextTier}-start`;
        const nextValue = value + step;
        updatedTooltips[
          nextKey
        ] = `Skip this to include values higher than ${fmt(nextValue)}`;
      } else if (position === "start") {
        const prevTier = type === RFMType.Recency ? tier - 1 : tier + 1;
        const prevEndKey = `${type}-${prevTier}-end`;
        const prevRaw = allValues[prevEndKey];
        let base = value;
        if (prevRaw != null) {
          const prev = parseFloat(prevRaw.toString());
          if (!isNaN(prev)) {
            base = prev + step;
          }
        }
        updatedTooltips[key] = `Skip this to include values higher than ${fmt(
          base
        )}`;
      }
    });

    setTooltipTexts((prev) => ({
      ...prev,
      ...updatedTooltips,
    }));
  };

  const updateRFMSettings = (
    settings: any[],
    values: Record<string, any>
  ): any[] => {
    return settings.map((item) => ({
      ...item,
      RFMStartValue: getParsedValue(values, item, "start"),
      RFMEndValue: getParsedValue(values, item, "end"),
    }));
  };

  const getParsedValue = (
    values: Record<string, any>,
    item: any,
    type: "start" | "end"
  ): number => {
    const parsedValue = parseFloat(
      values[`${item.RFMType}-${item.RFMTier}-${type}`]
    );
    return isNaN(parsedValue) ? 0 : parsedValue;
  };

  const handleChangeCutOffDateInEdit = (value: string) => {
    setIsTyping(false);
    setCutOffDateInEdit(value);
    const selectedOption = optionsCutOffDateOnEdit.find(
      (opt) => opt.value === value
    );
    setSelectedPeriod(selectedOption?.period ?? 3);
    if (selectedOption) {
      const formValues = form.getFieldsValue();
      const updatedSettings = updateRFMSettings(brandRFMSetting, formValues);

      const { updatedSettings: newRFMSettings } = applyFrequencyDataUpdate(
        updatedSettings,
        brandRFMSetting,
        selectedPeriod,
        selectedOption.period
      );

      setUpdatedBrandRFMSetting(newRFMSettings);
    }
  };
  // =====================
  // Frequency Updates
  // =====================
  const updateTooltipText = (newValues: any) => {
    const entries = Object.entries(newValues);

    entries.forEach(([key, rawValue]) => {
      const isStart = key.includes("start");
      const isEnd = key.includes("end");
      const keyParts = key.split("-");

      const type = parseInt(keyParts[0]);
      const tier = parseInt(keyParts[1]);
      const position = keyParts[2];

      let numericValue = parseFloat(
        (rawValue ?? "").toString().replace(/,/g, ".")
      );
      let step = 1;
      if (type === RFMType.Frequency) step = 0.1;
      if (type === RFMType.Monetary) step = 0.01;
      if (isNaN(numericValue)) {
        const oppositePosition = position === "start" ? "end" : "start";
        const oppositeKey = `${type}-${tier}-${oppositePosition}`;

        const oppositeRaw = form.getFieldValue(oppositeKey);
        const oppositeParsed = parseFloat((oppositeRaw ?? "").toString());

        if (!isNaN(oppositeParsed)) {
          numericValue =
            position === "start"
              ? oppositeParsed + step
              : oppositeParsed - step;
        } else if (type === RFMType.Monetary && tier === RFMTier.VeryHigh) {
          const nextEndKey = `${type}-${tier + 1}-end`;
          const fallback = form.getFieldValue(nextEndKey);
          const fallbackParsed = parseFloat((fallback ?? "").toString());
          if (!isNaN(fallbackParsed)) {
            numericValue = fallbackParsed + 0.01;
          }
        } else {
          return;
        }
      }
      if (isNaN(numericValue)) {
        console.warn(`Skipping tooltip for ${key}: value is NaN`);
        return;
      }

      let formattedValue: string;
      if (type === RFMType.Monetary) {
        formattedValue = Number.isInteger(numericValue)
          ? numericValue.toString()
          : numericValue.toFixed(2);
      } else if (type === RFMType.Frequency) {
        const rounded = Math.round(numericValue * 10) / 10;
        formattedValue = Number.isInteger(rounded)
          ? rounded.toString()
          : rounded.toFixed(1);
      } else {
        formattedValue = numericValue.toString();
      }

      const tooltipMessage = isStart
        ? `Skip this to include values higher than ${formattedValue}`
        : isEnd
        ? `Skip this to include values lower than ${formattedValue}`
        : "";

      setTooltipTexts((prev) => ({
        ...prev,
        [key]: tooltipMessage,
      }));
    });
  };

  const applyFrequencyDataUpdate = (
    baseSettings: any[],
    frequencySettings: any[],
    selectedPeriod: number,
    selectedOptionPeriod?: number
  ) => {
    let newFrequencyData: any[] = [];

    if (selectedOptionPeriod !== undefined) {
      newFrequencyData = getFrequencyTiers(
        baseSettings,
        selectedOptionPeriod,
        selectedPeriod
      );
    } else {
      newFrequencyData = getFrequencyTiers(baseSettings, selectedPeriod);
    }

    const updatedSettings = frequencySettings.map((item: any) =>
      item.RFMType === 2
        ? newFrequencyData.find((f: any) => f.Id === item.Id) ?? item
        : item
    );

    const transformedData = transformRFMData(updatedSettings);
    const formValues = newFrequencyData.reduce((acc, item) => {
      acc[`${item.RFMType}-${item.RFMTier}-start`] =
        item.RFMStartValue === 0 ? "" : item.RFMStartValue;
      acc[`${item.RFMType}-${item.RFMTier}-end`] =
        item.RFMEndValue === 0 ? "" : item.RFMEndValue;
      return acc;
    }, {} as Record<string, any>);
    form.setFieldsValue(formValues);
    updateTooltipText(formValues);

    return {
      newFrequencyData,
      updatedSettings,
      transformedData,
      formValues,
    };
  };

  const convertBaseRanges = (
    baseRanges: TierData[],
    conversionFactor: number
  ) => {
    let previousEnd = 0;
    const isAnyDecimal = baseRanges.some((t) => (t.baseEnd ?? 0) % 1 !== 0);

    return baseRanges.map(({ tier, baseStart, baseEnd }, index) => {
      let startValue: number, endValue: number;
      if (tier === 1 && baseEnd === 0) {
        const tier2 = baseRanges.find((t) => t.tier === 2);
        startValue =
          (tier2?.baseEnd ?? 0) / conversionFactor + (isAnyDecimal ? 0.1 : 1);
        endValue = 0;
      } else if (index === 0) {
        startValue = baseStart;
        endValue = baseEnd / conversionFactor;
      } else {
        startValue = previousEnd + (isAnyDecimal ? 0.1 : 1);
        endValue = baseEnd / conversionFactor;
      }

      previousEnd = endValue;
      return { tier, baseStart: startValue, baseEnd: endValue };
    });
  };

  const scaleTiers = (baseRanges: TierData[], scaleFactor: number) => {
    let previousEnd = 0;
    const isAnyDecimal = baseRanges.some((t) => (t.baseEnd ?? 0) % 1 !== 0);

    return baseRanges.map(({ tier, baseStart, baseEnd }, index) => {
      let startValue: number, endValue: number;

      if (tier === 1 && baseEnd === 0) {
        const tier2 = baseRanges.find((t) => t.tier === 2);
        startValue =
          (tier2?.baseEnd ?? 0) * scaleFactor + (isAnyDecimal ? 0.1 : 1);
        endValue = 0;
      } else if (index === 0) {
        startValue = baseStart;
        endValue = baseEnd * scaleFactor;
      } else {
        startValue = previousEnd + (isAnyDecimal ? 0.1 : 1);
        endValue = baseEnd * scaleFactor;
      }

      previousEnd = endValue;
      return { tier, startValue, endValue };
    });
  };

  const getFrequencyTiers = (
    rfmData: any[],
    period: number,
    originalPeriod?: number
  ): any[] => {
    if (!rfmData || rfmData.length === 0) return [];

    const scaleFactor = period / 3;
    const conversionFactor = originalPeriod ? originalPeriod / 3 : 1;

    let baseRanges: TierData[] = rfmData
      .filter((item) => item.RFMType === 2)
      .sort((a, b) => b.RFMTier - a.RFMTier)
      .map(({ RFMTier, RFMStartValue, RFMEndValue }) => ({
        tier: RFMTier,
        baseStart: RFMStartValue,
        baseEnd: RFMEndValue,
      }));

    if (originalPeriod && originalPeriod !== 3) {
      baseRanges = convertBaseRanges(baseRanges, conversionFactor);
    }

    const scaledTiers = scaleTiers(baseRanges, scaleFactor);

    return rfmData.map((item) => {
      if (item.RFMType === 2) {
        const matchedTier = scaledTiers.find((t) => t.tier === item.RFMTier);
        return {
          ...item,
          RFMStartValue: matchedTier?.startValue ?? item.RFMStartValue,
          RFMEndValue: matchedTier?.endValue ?? item.RFMEndValue,
        };
      }
      return item;
    });
  };

  const resetEditForm = (resetToDefault: boolean) => {
    if (resetToDefault) {
      form.resetFields();
    }
  };

  const setIsConfirmationModalVisible = (val: boolean) => {};

  const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1024;
  const isMobile = window.innerWidth < 768;
  const hasValidRFM =
    rfmSettings &&
    rfmSettings.length === 3 &&
    rfmSettings.some((setting) =>
      Object.values(setting || {}).some(
        (val) => val !== undefined && val !== null
      )
    );

  const transposedData = hasValidRFM
    ? [
        {
          key: "VeryHigh",
          definition: "Very High",
          Recency: rfmSettings?.[0]?.VeryHigh ?? "-",
          Frequency: rfmSettings?.[1]?.VeryHigh ?? "-",
          Monetary: rfmSettings?.[2]?.VeryHigh ?? "-",
        },
        {
          key: "High",
          definition: "High",
          Recency: rfmSettings?.[0]?.High ?? "-",
          Frequency: rfmSettings?.[1]?.High ?? "-",
          Monetary: rfmSettings?.[2]?.High ?? "-",
        },
        {
          key: "Medium",
          definition: "Medium",
          Recency: rfmSettings?.[0]?.Medium ?? "-",
          Frequency: rfmSettings?.[1]?.Medium ?? "-",
          Monetary: rfmSettings?.[2]?.Medium ?? "-",
        },
        {
          key: "Low",
          definition: "Low",
          Recency: rfmSettings?.[0]?.Low ?? "-",
          Frequency: rfmSettings?.[1]?.Low ?? "-",
          Monetary: rfmSettings?.[2]?.Low ?? "-",
        },
        {
          key: "VeryLow",
          definition: "Very Low",
          Recency: rfmSettings?.[0]?.VeryLow ?? "-",
          Frequency: rfmSettings?.[1]?.VeryLow ?? "-",
          Monetary: rfmSettings?.[2]?.VeryLow ?? "-",
        },
      ]
    : [];

  const columnsInTablet: ColumnsType<any> = [
    {
      title: "Definition",
      dataIndex: "definition",
      key: "definition",
      fixed: "left",
      width: 150,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#C9C7C7",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      onCell: (record: any) => ({
        style: {
          backgroundColor:
            record.definition === "Very High"
              ? "#258FDA"
              : record.definition === "High"
              ? "#4BB464"
              : record.definition === "Medium"
              ? "#FFE500"
              : record.definition === "Low"
              ? "#FF9200"
              : record.definition === "Very Low"
              ? "#D92636"
              : "#F2F2F2",
          padding: "12px",
          height: "100%",
          fontWeight: "bold",
        },
      }),
    },
    {
      title: "Recency",
      dataIndex: "Recency",
      key: "recency",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#C9C7C7",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      onCell: (record: any) => ({
        style: {
          backgroundColor:
            record.definition === "Very High"
              ? "#E9F4FB"
              : record.definition === "High"
              ? "#EDF8F0"
              : record.definition === "Medium"
              ? "#FFFCE5"
              : record.definition === "Low"
              ? "#FFF4E5"
              : record.definition === "Very Low"
              ? "#FBE9EB"
              : "#F2F2F2",
          padding: "12px",
          height: "100%",
          fontWeight: record.Recency === "Out Of Range" ? "bold" : "unset",
        },
      }),
    },
    {
      title: "Frequency",
      dataIndex: "Frequency",
      key: "frequency",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#C9C7C7",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      onCell: (record: any) => ({
        style: {
          backgroundColor:
            record.definition === "Very High"
              ? "#E9F4FB"
              : record.definition === "High"
              ? "#EDF8F0"
              : record.definition === "Medium"
              ? "#FFFCE5"
              : record.definition === "Low"
              ? "#FFF4E5"
              : record.definition === "Very Low"
              ? "#FBE9EB"
              : "#F2F2F2",
          padding: "12px",
          height: "100%",
        },
      }),
      render: (value, record) => (
        <div>
          <div>{value}</div>
          <div style={{ fontSize: "10px", color: "#555" }}>
            every {selectedPeriod} months
          </div>
        </div>
      ),
    },
    {
      title: "Monetary",
      dataIndex: "Monetary",
      key: "monetary",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#C9C7C7",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      onCell: (record: any) => ({
        style: {
          backgroundColor:
            record.definition === "Very High"
              ? "#E9F4FB"
              : record.definition === "High"
              ? "#EDF8F0"
              : record.definition === "Medium"
              ? "#FFFCE5"
              : record.definition === "Low"
              ? "#FFF4E5"
              : record.definition === "Very Low"
              ? "#FBE9EB"
              : "#F2F2F2",
          padding: "12px",
          height: "100%",
        },
      }),
    },
  ];

  const columns: ColumnsType<{
    key: string;
    definition: string;
    veryHigh: string;
    high: string;
    medium: string;
    low: string;
    veryLow: string;
  }> = [
    {
      title: "Definition",
      dataIndex: "definition",
      key: "definition",
      fixed: "left",
      width: 150,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#C9C7C7",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      onCell: (value: any) => ({
        style: {
          backgroundColor: "#F2F2F2",
          padding: "12px",
          height: "100%",
        },
      }),
    },
    {
      title: "Very High",
      dataIndex: "VeryHigh",
      key: "veryHigh",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#258FDA",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record.definition === "Frequency" && (
            <div style={{ fontSize: 12, color: "#555" }}>
              every {selectedPeriod} months
            </div>
          )}
        </div>
      ),
      onCell: (value: any) => ({
        style: {
          backgroundColor: "#E9F4FB",
          padding: "12px",
          height: "100%",
          fontWeight: value.VeryHigh === "Out Of Range" ? "bold" : "normal",
        },
      }),
    },
    {
      title: "High",
      dataIndex: "High",
      key: "high",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#4BB464",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record.definition === "Frequency" && (
            <div style={{ fontSize: 12, color: "#555" }}>
              every {selectedPeriod} months
            </div>
          )}
        </div>
      ),
      onCell: (value: any) => ({
        style: {
          backgroundColor: "#EDF8F0",
          padding: "12px",
          height: "100%",
          fontWeight: value.High === "Out Of Range" ? "bold" : "normal",
        },
      }),
    },
    {
      title: "Medium",
      dataIndex: "Medium",
      key: "medium",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#FFE500",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record.definition === "Frequency" && (
            <div style={{ fontSize: 12, color: "#555" }}>
              every {selectedPeriod} months
            </div>
          )}
        </div>
      ),
      onCell: (value: any) => ({
        style: {
          backgroundColor: "#FFFCE5",
          padding: "12px",
          height: "100%",
          fontWeight: value.Medium === "Out Of Range" ? "bold" : "normal",
        },
      }),
    },
    {
      title: "Low",
      dataIndex: "Low",
      key: "low",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#FF9200",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record.definition === "Frequency" && (
            <div style={{ fontSize: 12, color: "#555" }}>
              every {selectedPeriod} months
            </div>
          )}
        </div>
      ),
      onCell: (value: any) => ({
        style: {
          backgroundColor: "#FFF4E5",
          padding: "12px",
          height: "100%",
          fontWeight: value.Low === "Out Of Range" ? "bold" : "normal",
        },
      }),
    },
    {
      title: "Very Low",
      dataIndex: "VeryLow",
      key: "veryLow",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#D92636",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record.definition === "Frequency" && (
            <div style={{ fontSize: 12, color: "#555" }}>
              every {selectedPeriod} months
            </div>
          )}
        </div>
      ),
      onCell: (value: any) => {
        return {
          style: {
            backgroundColor: "#FBE9EB",
            padding: "12px",
            height: "100%",
            fontWeight: value.VeryLow === "Out Of Range" ? "bold" : "normal",
          },
        };
      },
    },
  ];

  const defaultSettings = async (brandId: string) => {
    const setting = await brandRFMSettingService.getDefaultBrandRFMSetting(
      brandId
    );
    return setting ?? "";
  };

  function generateInitialBrandRFMProfileSettings(): BrandRFMProfileSetting[] {
    const createdDate = dayjs().toDate();

    const settings: BrandRFMProfileSetting[] = [];

    for (const tierKey in RFMTier) {
      if (isNaN(Number(tierKey))) {
        const tier = RFMTier[tierKey as keyof typeof RFMTier];
        for (const typeKey in RFMType) {
          if (isNaN(Number(typeKey))) {
            const type = RFMType[typeKey as keyof typeof RFMType];
            settings.push({
              Id: 0,
              RFMType: type,
              RFMTier: tier,
              RFMStartValue: undefined,
              RFMEndValue: undefined,
              CreatedDate: createdDate,
            });
          }
        }
      }
    }

    return settings;
  }

  const fetchRFMSettings = async () => {
    try {
      if (!brandId) return;
      const response = await generateInitialBrandRFMProfileSettings();
      const data: any[] = Array.isArray(response) ? response : [];
      const sortedData = data.sort(
        (a, b) => (b.RFMTier || 0) - (a.RFMTier || 0)
      );
      const filteredDataFrequency = Array.isArray(sortedData)
        ? sortedData.filter((item) => item.RFMType === 2)
        : [];

      setBrandRFMSetting(Array.isArray(sortedData) ? sortedData : []);

      setUpdatedBrandRFMSetting(filteredDataFrequency);
    } catch (error) {
      console.error("Error fetching RFM settings:", error);
    }
  };

  const fetchBrandRFMProfile = async () => {
    if (brandId) {
      setLoading(true);
      try {
        const data = await brandRFMProfileService.getBrandRFMProfile(brandId);
        setListProfile(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const applyProfileSettings = (profile: any) => {
    if (!profile) return;

    const { transformedData } = applyFrequencyDataUpdate(
      profile.BrandRFMProfileSetting,
      profile.BrandRFMProfileSetting,
      selectedPeriod
    );

    setRfmSettings(transformedData);
  };

  const fetchRFMBrandProfile = async () => {
    if (!brandId || !profileId) return;
    try {
      setLoading(true);

      const response = await brandRFMProfileService.getBrandRFMProfileById(
        brandId,
        Number(profileId)
      );
      const data = Array.isArray(response) ? response[0] : response;
      if (!data) return;

      setSelectedBrandRFMProfile(data);

      form.setFieldsValue({
        profile: {
          name: data?.Name,
          description: data?.Description,
        },
      });

      if (data.IsActive) {
        setIsActive(true);
      }

      applyProfileSettings(data);
    } catch (error) {
      console.error("Error fetching and applying RFM profile:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!brandId) return;

    const fetchSettings = async () => {
      const setting = await defaultSettings(brandId);

      if (setting) {
        setDefaultSetting(setting);
      }
    };
    fetchSettings();
    fetchBrandRFMProfile();
    if (profileId) {
      fetchRFMBrandProfile();
    } else if (selectedBrandRFMProfile) {
      applyProfileSettings(selectedBrandRFMProfile);
    }
  }, [brandId]);

  // =====================
  // Data Transformation
  // =====================
  const transformRFMData = (data: any[]) => {
    return Object.values(RFMType)
      .filter((type) => typeof type === "number")
      .map((type) => {
        const rowData: any = {
          key: type,
          definition: RFMType[type as number],
        };

        Object.values(RFMTier)
          .filter((tier) => typeof tier === "number")
          .forEach((tier) => {
            const setting = data.find(
              (item) => item.RFMType === type && item.RFMTier === tier
            );

            let formattedValue = "-";
            let startValue = 0;
            let endValue = 0;
            if (setting) {
              startValue = parseFloat(setting.RFMStartValue);
              endValue = parseFloat(setting.RFMEndValue);

              if (tier === RFMTier.VeryHigh) {
                if (type === RFMType.Frequency) {
                  formattedValue =
                    endValue === 0
                      ? `Order Count: ${
                          Number.isInteger(startValue)
                            ? startValue
                            : startValue.toFixed(1)
                        }+`
                      : `Order Count: ${
                          Number.isInteger(startValue)
                            ? startValue
                            : startValue.toFixed(1)
                        } - ${
                          Number.isInteger(endValue)
                            ? endValue
                            : endValue.toFixed(1)
                        }`;
                } else if (type === RFMType.Monetary) {
                  formattedValue = `Total Spending: $${startValue.toLocaleString(
                    "en-US"
                  )}+`;
                } else {
                  if (startValue === 0) {
                    formattedValue = `Last Order: <= ${endValue} days`;
                  } else {
                    formattedValue = `Last Order: ${startValue} - ${endValue} days`;
                  }
                }
              } else {
                if (type === RFMType.Recency) {
                  formattedValue =
                    endValue === 0
                      ? `Last Order: ${startValue}+ days`
                      : `Last Order: ${startValue} - ${endValue} days`;
                } else if (type === RFMType.Frequency) {
                  if (startValue === 0) {
                    formattedValue = `Order Count: <= ${endValue}`;
                  } else {
                    formattedValue =
                      startValue === endValue
                        ? `Order Count: ${
                            Number.isInteger(startValue)
                              ? startValue
                              : startValue.toFixed(1)
                          }`
                        : `Order Count: ${
                            Number.isInteger(startValue)
                              ? startValue
                              : startValue.toFixed(1)
                          } - ${
                            Number.isInteger(endValue)
                              ? endValue
                              : endValue.toFixed(1)
                          }`;
                  }
                } else if (type === RFMType.Monetary) {
                  if (startValue === 0) {
                    formattedValue = `Total Spending: <= $${endValue.toLocaleString(
                      "en-US"
                    )}`;
                  } else {
                    formattedValue = `Total Spending: $${startValue.toLocaleString(
                      "en-US"
                    )} - $${endValue.toLocaleString("en-US")}`;
                  }
                }
              }
            }

            rowData[RFMTier[tier as number]] = formattedValue;
          });

        return rowData;
      });
  };

  function convertSettingsToInitialValues(settingsArray: any[]) {
    const initialValues: Record<string, number> = {};

    settingsArray.forEach((item) => {
      const keyPrefix = `${item.RFMType}-${item.RFMTier}`;
      initialValues[`${keyPrefix}-start`] = item.RFMStartValue;
      initialValues[`${keyPrefix}-end`] = item.RFMEndValue;
    });

    return initialValues;
  }

  useEffect(() => {
    if (selectedBrandRFMProfile) {
      setBrandRFMProfileSetting(selectedBrandRFMProfile.BrandRFMProfileSetting);
    }
  }, [selectedBrandRFMProfile]);

  return (
    <>
      {
        <>
          <Form
            className="form-horizontal"
            form={form}
            name="form-edit-profile"
            onFinish={handleSubmitProfile}
            validateMessages={validateMessages}
            initialValues={{
              profile: {
                name: selectedBrandRFMProfile?.Name,
                description: selectedBrandRFMProfile?.Description,
              },
            }}
          >
            <Card
              title={
                <Row
                  justify="space-between"
                  align="middle"
                  wrap
                  style={{ width: "100%" }}
                >
                  <Col xs={12} md={12} xl={12}>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        display: "block",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {`Edit Profile`}
                    </span>
                  </Col>
                  <Col
                    xs={12}
                    md={12}
                    xl={12}
                    style={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <Button
                      size="middle"
                      color="primary"
                      variant="outlined"
                      style={{ height: 40, width: 150 }}
                      onClick={() => {
                        const searchParams = new URLSearchParams(
                          location.search
                        );
                        searchParams.delete("profileId");
                        navigate(
                          `${
                            RoutePaths.IFRAMERFMPROFILE
                          }?${searchParams.toString()}`
                        );
                      }}
                    >
                      Back
                    </Button>
                  </Col>
                </Row>
              }
              style={{
                display: isCriteriaModalVisible ? "none" : "block",
              }}
            >
              <Form.Item shouldUpdate noStyle>
                <Row gutter={16} align="top">
                  <Col span={12}>
                    <span>Name</span>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={["profile", "name"]}
                      rules={[
                        {
                          required: true,
                          message: "Please input your profile name",
                        },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const trimmed = value.trim().toLowerCase();

                            const duplicate = listProfile?.some(
                              (p: any) =>
                                p.Id !== selectedBrandRFMProfile?.Id &&
                                p.Name?.trim().toLowerCase() === trimmed
                            );

                            if (duplicate) {
                              return Promise.reject(
                                new Error(
                                  "Profile name already exists. Please choose another."
                                )
                              );
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter profile name"
                        disabled={
                          selectedBrandRFMProfile?.Name ===
                            "Full Service Default Profiles" ||
                          selectedBrandRFMProfile?.Name ===
                            "Quick Service Default Profiles"
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
              <Form.Item shouldUpdate noStyle>
                <Row gutter={16} align="top" style={{ marginTop: 10 }}>
                  <Col span={12}>
                    <span>Description</span>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={["profile", "description"]}
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Please input your profile Description",
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="Enter profile Description"
                        disabled={
                          selectedBrandRFMProfile?.Name ===
                            "Full Service Default Profiles" ||
                          selectedBrandRFMProfile?.Name ===
                            "Quick Service Default Profiles"
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Card>
            <Card
              title={
                <Row
                  justify="space-between"
                  align="middle"
                  wrap
                  style={{ width: "100%" }}
                >
                  <Col xs={12} md={12} xl={12}>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        display: "block",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {`Configuration RFM Criteria`}
                    </span>
                  </Col>
                  {rfmSettings.length > 0 && (
                    <Col xs={12} md={12} xl={12} style={{ textAlign: "right" }}>
                      <Button
                        className="button-edi-criteria"
                        icon={<EditOutlined />}
                        color="primary"
                        variant="outlined"
                        style={{ width: isMobile ? 120 : 210 }}
                        onClick={() => setIsCriteriaModalVisible(true)}
                      >
                        Edit Criteria
                      </Button>
                    </Col>
                  )}
                </Row>
              }
              style={{
                display: isCriteriaModalVisible ? "none" : "block",
              }}
            >
              {isTablet || isMobile ? (
                <Table
                  className="criteria-table"
                  columns={columnsInTablet}
                  dataSource={transposedData}
                  pagination={false}
                  loading={loading}
                  scroll={{ x: "max-content" }}
                  locale={{
                    emptyText: (
                      <Empty
                        description="There are no criteria yet. Add a new criteria to get started!"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      >
                        <Button
                          type="primary"
                          onClick={() => setIsCriteriaModalVisible(true)}
                        >
                          <PlusCircleOutlined />
                          Add Criteria
                        </Button>
                      </Empty>
                    ),
                  }}
                />
              ) : (
                <Table
                  className="criteria-table"
                  columns={columns}
                  dataSource={rfmSettings}
                  pagination={false}
                  loading={loading}
                  scroll={{ x: "max-content" }}
                  locale={{
                    emptyText: (
                      <Empty
                        description="There are no criteria yet. Add a new criteria to get started!"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      >
                        <Button
                          type="primary"
                          onClick={() => setIsCriteriaModalVisible(true)}
                        >
                          <PlusCircleOutlined />
                          Add Criteria
                        </Button>
                      </Empty>
                    ),
                  }}
                />
              )}
              <Row gutter={16} style={{ marginTop: 10 }}>
                <Col span={12}>
                  <Checkbox
                    disabled={selectedBrandRFMProfile?.IsActive}
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  >
                    Set as Active (Select this profile as your active RFM
                    profile)
                  </Checkbox>
                </Col>
                <Col
                  xs={24}
                  md={12}
                  xl={12}
                  style={{
                    display: "flex",
                    justifyContent: isMobile ? "center" : "flex-end",
                    gap: 8,
                  }}
                >
                  {/* <Button
                    size="middle"
                    type="default"
                    style={{ height: 40, width: 150 }}
                    onClick={() => {
                      const searchParams = new URLSearchParams(location.search);
                      searchParams.delete("profileId");
                      navigate(
                        `${
                          RoutePaths.IFRAMERFMPROFILE
                        }?${searchParams.toString()}`
                      );
                    }}
                  >
                    Cancel
                  </Button> */}
                  <Button
                    htmlType="submit"
                    size="middle"
                    type="primary"
                    style={{ height: 40, width: 150 }}
                    loading={loading}
                  >
                    Submit
                  </Button>
                </Col>
              </Row>
            </Card>
          </Form>
        </>
      }

      {isCriteriaModalVisible && (
        <CriteriaForm
          visible={isCriteriaModalVisible}
          onCancel={handleCancel}
          form={form}
          onFinish={handleSubmitCriteria}
          initialValues={convertSettingsToInitialValues(brandRFMProfileSetting)}
          onValuesChange={onValuesChange}
          brandRFMSetting={brandRFMSetting}
          updatedBrandRFMSetting={updatedBrandRFMSetting}
          defaultSetting={defaultSetting}
          tooltipTexts={tooltipTexts}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isTyping={isTyping}
          setIsTyping={setIsTyping}
          isTypingEndValue={isTypingEndValue}
          setIsTypingEndValue={setIsTypingEndValue}
          cutOffDateInEdit={cutOffDateInEdit}
          handleChangeCutOffDateInEdit={handleChangeCutOffDateInEdit}
          optionsCutOffDateOnEdit={optionsCutOffDateOnEdit}
          isMobile={isMobile}
          loading={loading}
          resetEditForm={resetEditForm}
          setIsConfirmationModalVisible={setIsConfirmationModalVisible}
          selectedBrandRFMProfile={selectedBrandRFMProfile ?? undefined}
          setSelectedBrandRFMProfile={setSelectedBrandRFMProfile}
        />
      )}
    </>
  );
};

export default EditRFMProfile;
